/**
 * Time Picker Utility Functions
 * Helper functions for generating and filtering time options
 */

/**
 * Generate hours array (1-12 for 12hr, 0-23 for 24hr)
 * @param {boolean} is24Hour - Whether to use 24-hour format
 * @returns {string[]} Array of hour strings
 */
export function generateHours(is24Hour) {
  if (is24Hour) {
    return Array.from({ length: 24 }, (_, i) => String(i).padStart(2, "0"));
  } else {
    return Array.from({ length: 12 }, (_, i) => String(i + 1).padStart(2, "0"));
  }
}

/**
 * Generate minutes array (0-59, incrementing by 1)
 * @returns {string[]} Array of minute strings
 */
export function generateMinutes() {
  return Array.from({ length: 60 }, (_, i) => String(i).padStart(2, "0"));
}

/**
 * Generate all time options (no filtering needed)
 * @param {boolean} is24Hour - Whether to use 24-hour format
 * @returns {{hours: string[], minutes: string[], ampm: string[]}}
 */
export function getAllTimeOptions(is24Hour) {
  return {
    hours: generateHours(is24Hour),
    minutes: generateMinutes(),
    ampm: ["AM", "PM"],
  };
}

/**
 * Filter time options based on input
 * @param {string} input - Input string to filter by
 * @param {boolean} is24Hour - Whether to use 24-hour format
 * @param {string} currentValue - Current value (unused but kept for compatibility)
 * @returns {{hours: string[], minutes: string[], ampm: string[]}}
 */
export function filterTimeOptions(input, is24Hour, currentValue) {
  if (!input || input.trim() === "") {
    return {
      hours: generateHours(is24Hour),
      minutes: generateMinutes(),
      ampm: ["AM", "PM"],
    };
  }

  const inputLower = input.toLowerCase().trim();
  const hours = generateHours(is24Hour);
  const minutes = generateMinutes();
  const ampm = ["AM", "PM"];

  // Try to parse the input
  const timeMatch = input.match(/(\d{1,2})[:\s]*(\d{0,2})?\s*(am|pm)?/i);

  let filteredHours = hours;
  let filteredMinutes = minutes;
  let filteredAmpm = ampm;

  if (timeMatch) {
    const [, hourStr, minuteStr, ampmStr] = timeMatch;

    // Filter hours
    if (hourStr) {
      const hourNum = parseInt(hourStr);
      if (is24Hour) {
        filteredHours = hours.filter(h =>
          h.includes(hourStr) ||
          (hourNum >= 0 && hourNum <= 23 && h === String(hourNum).padStart(2, "0"))
        );
      } else {
        filteredHours = hours.filter(h =>
          h.includes(hourStr) ||
          (hourNum >= 1 && hourNum <= 12 && h === String(hourNum).padStart(2, "0"))
        );
      }
    }

    // Filter minutes
    if (minuteStr) {
      filteredMinutes = minutes.filter(m => m.includes(minuteStr));
    } else if (inputLower.includes(":") || inputLower.match(/\d{2}/)) {
      // If there's a colon or 2 digits, show all minutes
      filteredMinutes = minutes;
    }

    // Filter AM/PM
    if (ampmStr) {
      filteredAmpm = ampm.filter(a => a.toLowerCase().includes(ampmStr.toLowerCase()));
    }
  } else {
    // Simple text matching
    filteredHours = hours.filter(h => h.includes(input.replace(/\D/g, "")));
    filteredMinutes = minutes.filter(m => m.includes(input.replace(/\D/g, "")));
  }

  return {
    hours: filteredHours.length > 0 ? filteredHours : hours,
    minutes: filteredMinutes.length > 0 ? filteredMinutes : minutes,
    ampm: filteredAmpm.length > 0 ? filteredAmpm : ampm,
  };
}

/**
 * Scroll to a value in the time picker dropdown
 * ONLY called when typing in input fields, NOT when clicking dropdown values
 * @param {string} pickerType - Type of picker (start24, end24, start12, end12)
 * @param {string} hour - Hour value to scroll to
 * @param {string} minute - Minute value to scroll to
 * @param {string|null} ampm - AM/PM value to scroll to (for 12hr format)
 * @param {Object} params - Parameters object
 * @param {Object} params.timePickerRefs - Object containing refs for time picker dropdowns
 * @param {Object} params.timePickerOpen - Object containing open state for each picker
 * @param {Object} params.isSettingFromDropdownRef - Ref to prevent scrolling when setting from dropdown
 */
export function scrollToTimePickerValue(pickerType, hour, minute, ampm, { timePickerRefs, timePickerOpen, isSettingFromDropdownRef }) {
  const dropdownRef = timePickerRefs[pickerType]?.current;
  
  // Check if dropdown exists and is open (or assume open if ref exists and we're scrolling from typing)
  const isOpen = timePickerOpen?.[pickerType] !== false; // Allow scrolling even if state hasn't updated yet
  if (!dropdownRef) return;

  // Safety check: Don't scroll if value is being set from dropdown
  if (isSettingFromDropdownRef?.current) return;

  const is24Hour = pickerType.includes("24");
  const options = getAllTimeOptions(is24Hour);

  // Find the flex container that holds the columns - try multiple methods
  let flexContainer = dropdownRef.querySelector('div[style*="display: flex"]');
  if (!flexContainer) {
    // Try finding by structure - the flex container is after the header
    const allDivs = dropdownRef.querySelectorAll('div');
    for (let div of allDivs) {
      const style = window.getComputedStyle(div);
      if (style.display === 'flex' && div.children.length >= 2) {
        flexContainer = div;
        break;
      }
    }
  }

  if (!flexContainer) return;

  // Get all direct children divs of the flex container (these are the columns)
  const columns = Array.from(flexContainer.children).filter(child => child.tagName === 'DIV');
  if (columns.length === 0) return;

  // First column is hours, second is minutes, third is AM/PM (if 12hr)
  const hourColumn = columns[0];
  const minuteColumn = columns[1];
  const ampmColumn = !is24Hour && columns.length > 2 ? columns[2] : null;

  // Helper to scroll to element - ensures scrolling works even with scroll locks
  const scrollToElement = (element, column) => {
    if (!element || !column) return;

    // Temporarily unlock scroll if it's locked (from dropdown clicks)
    const wasOverflowHidden = column.style.overflow === 'hidden' || column.style.overflowY === 'hidden';
    const originalOverflow = column.style.overflow || '';
    const originalOverflowY = column.style.overflowY || '';

    if (wasOverflowHidden) {
      column.style.overflow = 'auto';
      column.style.overflowY = 'auto';
    }

    // Remove scroll preventer event listeners temporarily
    const scrollPreventer = column._scrollPreventer;
    if (scrollPreventer) {
      column.removeEventListener('scroll', scrollPreventer, { capture: true });
      column.removeEventListener('wheel', scrollPreventer, { capture: true });
      column.removeEventListener('touchmove', scrollPreventer, { capture: true });
    }

    // Cancel any active scroll restore animation
    if (column._scrollRestoreFrame) {
      cancelAnimationFrame(column._scrollRestoreFrame);
      column._scrollRestoreFrame = null;
    }

    // Restore scrollIntoView if it was disabled
    if (element._originalScrollIntoView) {
      element.scrollIntoView = element._originalScrollIntoView;
    }

    // Perform the scroll with a slight delay to ensure DOM is ready
    setTimeout(() => {
      try {
        // Calculate scroll position manually for more control
        const columnRect = column.getBoundingClientRect();
        const elementRect = element.getBoundingClientRect();
        const scrollTop = column.scrollTop + (elementRect.top - columnRect.top) - (columnRect.height / 2) + (elementRect.height / 2);

        // Use scrollTo for smoother scrolling
        column.scrollTo({
          top: Math.max(0, scrollTop),
          behavior: 'smooth'
        });
      } catch (e) {
        // Fallback to scrollIntoView
        try {
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        } catch (e2) {
          // Final fallback - direct scrollTop
          const scrollTop = element.offsetTop - (column.clientHeight / 2) + (element.clientHeight / 2);
          column.scrollTop = Math.max(0, scrollTop);
        }
      }

      // Restore scroll lock and event listeners after scroll completes
      setTimeout(() => {
        if (wasOverflowHidden) {
          column.style.overflow = originalOverflow;
          column.style.overflowY = originalOverflowY;
        }

        if (scrollPreventer) {
          column.addEventListener('scroll', scrollPreventer, { passive: false, capture: true });
          column.addEventListener('wheel', scrollPreventer, { passive: false, capture: true });
          column.addEventListener('touchmove', scrollPreventer, { passive: false, capture: true });
        }
      }, 600);
    }, 100);
  };

  // Scroll to hour
  if (hour && hourColumn) {
    const hourValue = hour.padStart(2, "0");
    const hourIndex = options.hours.indexOf(hourValue);
    if (hourIndex !== -1 && hourColumn.children[hourIndex]) {
      scrollToElement(hourColumn.children[hourIndex], hourColumn);
    }
  }

  // Scroll to minute
  if (minute && minuteColumn) {
    const minuteValue = minute.padStart(2, "0");
    const minuteIndex = options.minutes.indexOf(minuteValue);
    if (minuteIndex !== -1 && minuteColumn.children[minuteIndex]) {
      scrollToElement(minuteColumn.children[minuteIndex], minuteColumn);
    }
  }

  // Scroll to AM/PM
  if (!is24Hour && ampm && ampmColumn) {
    const ampmValue = ampm.toUpperCase();
    const ampmIndex = options.ampm.indexOf(ampmValue);
    if (ampmIndex !== -1 && ampmColumn.children[ampmIndex]) {
      scrollToElement(ampmColumn.children[ampmIndex], ampmColumn);
    }
  }
}
